import { useCallback, useState } from "react";
import type { EntityId, FamilyFormErrors, FamilyFormValueField, FamilyFormValues, ParentOption, ParentSlot } from "../types";
import { createInitialFamilyFormValues } from "../utils/familyFormMapping";

export const useFamilyForm = (initialValues: FamilyFormValues = createInitialFamilyFormValues()) => {
  const [values, setValues] = useState<FamilyFormValues>(initialValues);
  const [errors, setErrors] = useState<FamilyFormErrors>({});

  const updateField = useCallback((field: FamilyFormValueField, value: string) => {
    setValues((current) => ({
      ...current,
      [field]: value,
    }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }, []);

  const updateParentSearch = useCallback((slot: ParentSlot, value: string) => {
    setValues((current) => ({
      ...current,
      search: {
        ...current.search,
        [slot]: value,
      },
    }));
  }, []);

  const selectParent = useCallback((slot: ParentSlot, parent: ParentOption | null) => {
    setValues((current) => ({
      ...current,
      familyName:
        slot === "parent1" && parent?.lastName
          ? String(parent.lastName).trim()
          : slot === "parent1" && !parent
            ? ""
            : current.familyName,
      selectedParents: {
        ...current.selectedParents,
        [slot]: parent,
      },
      search: {
        ...current.search,
        [slot]: "",
      },
    }));
    setErrors((current) => ({ ...current, [slot]: undefined }));
  }, []);

  const swapParents = useCallback(() => {
    setValues((current) => ({
      ...current,
      selectedParents: {
        parent1: current.selectedParents.parent2,
        parent2: current.selectedParents.parent1,
      },
      search: {
        parent1: "",
        parent2: "",
      },
    }));
  }, []);

  const removeParent = useCallback((slot: ParentSlot) => {
    selectParent(slot, null);
  }, [selectParent]);

  return {
    values,
    setValues,
    errors,
    setErrors,
    updateField,
    updateParentSearch,
    selectParent,
    removeParent,
    swapParents,
  };
};

export type UseFamilyFormReturn = ReturnType<typeof useFamilyForm>;

export const idAsFormValue = (id: EntityId | null | undefined): string => (id == null ? "" : String(id));
